import {useCallback, useEffect, useState} from "react";
import {fetchTracked} from "../../features/catalog/infrastructure/CatalogService";
import {getOrElse, initial, isSuccess, pending} from "../../common/domain/RemoteData.model";
import {useComercialContext} from "../../comercial/ComercialContext";

/**
 * Un pendiente "vuelve a estar disponible" cuando hay stock suficiente
 * para la cantidad que el cliente quería (trackedQuantity, default 1).
 * @param {Object} product ProductDto
 * @returns {boolean}
 */
export const isRestocked = (product) =>
    (product?.availableUnits ?? 0) >= (product?.trackedQuantity ?? 1);

/**
 * Hook con la lista de productos pendientes del cliente (o del cliente
 * suplantado en modo comercial) y el subconjunto que ya tiene stock.
 */
export const useRestockedTrackedProducts = () => {
    const [trackedData, setTrackedData] = useState(initial([]));
    const {actingAsCliente} = useComercialContext();

    const refresh = useCallback(async () => {
        setTrackedData(pending());
        const result = await fetchTracked(actingAsCliente?.numCliente ?? null);
        setTrackedData(result);
    }, [actingAsCliente?.numCliente]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const removeLocally = useCallback((productCode) => {
        setTrackedData((current) => {
            if (!isSuccess(current)) {
                return current;
            }
            return {...current, value: current.value.filter((product) => product.code !== productCode)};
        });
    }, []);

    const allTracked = getOrElse(trackedData, []) ?? [];
    const restocked = allTracked.filter(isRestocked);

    return {trackedData, allTracked, restocked, refresh, removeLocally};
};
